import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, doc, getDoc, updateDoc, arrayUnion, Timestamp } from '@angular/fire/firestore';
import {
    ONBOARDING_STEPS,
    OnboardingState,
    OnboardingStep,
    applicableSteps,
    resolveRequiredSteps
} from '../config/onboarding-steps';
import { AnalyticsService } from './analytics.service';

@Injectable({ providedIn: 'root' })
export class OnboardingService {

    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);
    private analytics = inject(AnalyticsService);

    /**
     * Everything a setup wizard page needs: which organisation it is acting on,
     * and where it sits in that organisation's step list.
     *
     * The four setup pages used to read the organisation id straight out of the
     * Planning Center OIDC claim (`sign_in_attributes.organization_id`) and
     * hardcode their position as "step N of 4". Neither survives a standalone
     * organisation: it has no such claim, so the id came back empty and every
     * subsequent doc() call threw "Invalid document reference"; and it has three
     * steps, not four, because it skips the Planning Center webhook step.
     *
     * The user document is the source of truth for membership in both cases.
     */
    async getSetupContext(userId: string, stepId: string): Promise<{
        organizationId: string;
        organizationName: string;
        currentStep: number;
        totalSteps: number;
        progressPercentage: number;
    }> {
        const userSnap = await runInInjectionContext(
            this.injector,
            () => getDoc(doc(this.firestore, 'users', userId))
        );
        const userData = userSnap.exists() ? userSnap.data() : {};

        const organizationId = userData['organizationId'] ? String(userData['organizationId']) : '';
        const organizationName = userData['organizationName'] || 'Your Organization';

        // Fall back to the full list when the organisation has no stored
        // requiredSteps — that is every organisation created before this field,
        // and they are all Planning Center organisations.
        let steps = ONBOARDING_STEPS;
        if (organizationId) {
            steps = resolveRequiredSteps(await this.getOnboardingState(organizationId));
        }

        const index = steps.findIndex(s => s.id === stepId);
        const currentStep = index >= 0 ? index + 1 : 1;
        const totalSteps = steps.length;

        return {
            organizationId,
            organizationName,
            currentStep,
            totalSteps,
            progressPercentage: Math.round((currentStep / totalSteps) * 100)
        };
    }

    /**
     * Read the onboarding state from an organization document.
     * Returns null if the field doesn't exist yet.
     */
    async getOnboardingState(orgId: string): Promise<OnboardingState | null> {
        const orgRef = doc(this.firestore, 'organizations', orgId);
        const snap = await runInInjectionContext(this.injector, () => getDoc(orgRef));
        if (!snap.exists()) return null;
        return snap.data()['onboarding'] ?? null;
    }

    /**
     * Mark a step as done (idempotent — uses arrayUnion).
     * If all steps are now done, sets completedAt timestamp.
     *
     * `outcome` distinguishes finishing a step from skipping past it. Both
     * still count as done for the wizard — that is what lets someone reach the
     * dashboard without connecting WhatsApp — but they are different facts, and
     * for a long time only the analytics event knew which had happened.
     * completedSteps recorded them identically, so the platform console showed
     * a green tick against every step of an organisation that had skipped half
     * of them, and there was no way to tell from the data that anything had
     * been skipped at all.
     *
     * stepOutcomes keeps that alongside completedSteps rather than instead of
     * it: every existing reader of completedSteps — the guard, the blocking
     * function, isOnboardingComplete — keeps working untouched.
     */
    async completeStep(
        orgId: string,
        stepId: string,
        outcome: 'completed' | 'skipped' = 'completed'
    ): Promise<void> {
        const orgRef = doc(this.firestore, 'organizations', orgId);

        await runInInjectionContext(this.injector, () =>
            updateDoc(orgRef, {
                'onboarding.completedSteps': arrayUnion(stepId),
                [`onboarding.stepOutcomes.${stepId}`]: {
                    outcome,
                    at: Timestamp.now()
                }
            })
        );

        // Re-read to check if all steps are now complete. Only the steps this
        // organisation actually needs count — a standalone org has no webhook
        // step and would otherwise never reach completion.
        const state = await this.getOnboardingState(orgId);
        const required = resolveRequiredSteps(state);
        const allDone = required.every(s => state?.completedSteps?.includes(s.id));

        // Every wizard page funnels through here, so this one call site gives
        // the whole onboarding funnel rather than instrumenting five
        // components separately. `step_index`/`step_total` make the drop-off
        // between steps readable without knowing the step order in advance,
        // which differs between PCO and standalone organisations.
        const stepIndex = required.findIndex(s => s.id === stepId);
        this.analytics.logEvent('onboarding', {
            action: outcome === 'skipped' ? 'step_skipped' : 'step_completed',
            step_id: stepId,
            step_index: stepIndex >= 0 ? stepIndex + 1 : null,
            step_total: required.length,
            organization_id: orgId
        });

        if (allDone && !state?.completedAt) {
            await runInInjectionContext(this.injector, () =>
                updateDoc(orgRef, { 'onboarding.completedAt': Timestamp.now() })
            );

            // The conversion that matters: a signed-up organisation is only
            // actually onboarded at this point.
            this.analytics.logEvent('onboarding', {
                action: 'onboarding_completed',
                step_total: required.length,
                organization_id: orgId
            });
        }
    }

    /**
     * Returns the first onboarding step this organisation still has to do,
     * or null if all are done.
     *
     * Prefer resolveNextStep() where the organisation id is available: it
     * reads the state itself and cannot be called with the state omitted,
     * which silently resolves against the wrong step list.
     */
    getNextIncompleteStep(
        completedSteps: string[],
        state?: OnboardingState | null
    ): OnboardingStep | null {
        return resolveRequiredSteps(state).find(s => !completedSteps.includes(s.id)) ?? null;
    }

    /**
     * The next step this organisation should be sent to, or null when the
     * wizard is finished.
     *
     * Reads the organisation itself so callers cannot forget to pass the state
     * — five call sites previously did, which resolved standalone orgs against
     * the full Planning Center step list and routed them to /webhook-setup.
     *
     * Steps that configure a WhatsApp number are dropped when no number is
     * connected. When that leaves nothing to do, those steps are marked
     * complete before returning null: onboarding.completedAt is what starts
     * the organisation's trial (see functions onOnboardingComplete), so
     * leaving them merely un-done would strand the org without one.
     */
    async resolveNextStep(orgId: string): Promise<OnboardingStep | null> {
        const orgRef = doc(this.firestore, 'organizations', orgId);
        const snap = await runInInjectionContext(this.injector, () => getDoc(orgRef));
        if (!snap.exists()) return null;

        const data = snap.data();
        const state: OnboardingState | null = data['onboarding'] ?? null;
        const completed: string[] = state?.completedSteps ?? [];
        const whatsappConnected = data['whatsapp']?.status === 'connected';

        // Planning Center supplies country and timezone; nothing else does.
        const needsLocation = data['orgType'] === 'standalone' && !data['countryCode'];

        const applicable = applicableSteps(state, { whatsappConnected, needsLocation });
        const next = applicable.find(s => !completed.includes(s.id));
        if (next) return next;

        // Nothing left that can be acted on. Anything still outstanding is
        // inapplicable rather than skipped by choice, so record it as done and
        // let the completion timestamp fire.
        const outstanding = applicable
            .map(s => s.id)
            .concat(resolveRequiredSteps(state).map(s => s.id))
            .filter((id, i, arr) => arr.indexOf(id) === i)
            .filter(id => !completed.includes(id));

        if (outstanding.length > 0) {
            // Recorded as 'not_applicable' rather than 'completed'. These are
            // the steps nobody chose to skip and nobody could have done — the
            // business-details and messaging-limit steps for an organisation
            // with no WhatsApp number to attach them to. Stamping them
            // 'completed' is what made an organisation that had connected
            // nothing look fully onboarded in the platform console.
            const outcomes: Record<string, unknown> = {
                'onboarding.completedSteps': arrayUnion(...outstanding)
            };
            for (const id of outstanding) {
                outcomes[`onboarding.stepOutcomes.${id}`] = {
                    outcome: 'not_applicable',
                    at: Timestamp.now()
                };
            }

            await runInInjectionContext(this.injector, () => updateDoc(orgRef, outcomes));
        }

        if (!state?.completedAt) {
            await runInInjectionContext(this.injector, () =>
                updateDoc(orgRef, { 'onboarding.completedAt': Timestamp.now() })
            );
        }

        return null;
    }

    /**
     * For orgs created before the onboarding system existed:
     * derive completedSteps from existing Firestore fields and
     * write the initial onboarding state.
     */
    async migrateExistingOrg(orgId: string, orgData: any): Promise<OnboardingState> {
        const completedSteps: string[] = [];

        // webhook-setup: org has webhooks array with entries
        if (orgData.webhooks && Array.isArray(orgData.webhooks) && orgData.webhooks.length > 0) {
            completedSteps.push('webhook-setup');
        }

        // whatsapp-setup: org has whatsapp with connected status
        if (orgData.whatsapp && orgData.whatsapp.status === 'connected') {
            completedSteps.push('whatsapp-setup');
        }

        // messaging-setup: org has messagingLimits configured
        if (orgData.messagingLimits) {
            completedSteps.push('messaging-setup');
        }

        // Anything old enough to need migrating predates standalone orgs, so
        // it is a Planning Center org and needs the full step list.
        const allDone = ONBOARDING_STEPS.every(s => completedSteps.includes(s.id));
        const state: OnboardingState = {
            completedSteps,
            completedAt: allDone ? Timestamp.now() : null,
            requiredSteps: ONBOARDING_STEPS.map(s => s.id)
        };

        const orgRef = doc(this.firestore, 'organizations', orgId);
        await runInInjectionContext(this.injector, () =>
            updateDoc(orgRef, { onboarding: state })
        );

        return state;
    }
}
